import { useQuery } from "@tanstack/react-query";
import {
  StandardsShopClient,
  ProductDetailDrawerDto,
  ProductDetailDrawerRequestDto,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import {
  BUNDLE_PRODUCT_CLASS,
  COMMODITY_PRODUCT_CLASS,
} from "shared-ui/src/types/ecommerce/constants";

export interface StandardsProductDetailDrawerHookResult {
  data: ProductDetailDrawerDto | null;
  isLoading: boolean;
  isError: boolean;
}

const SUPPORTED_PRODUCT_CLASSES = [
  COMMODITY_PRODUCT_CLASS,
  BUNDLE_PRODUCT_CLASS,
];

/**
 * Fetches enriched product-detail-drawer data (images, specifications) for a Standards Shop product.
 * The query is gated on the ShiOnePermission.featuredProductsView permission and a valid, supported request so the drawer can render
 * basic tile data immediately without a wasted request.
 */
export default function useStandardsProductDetailDrawer(
  request: ProductDetailDrawerRequestDto | null,
): StandardsProductDetailDrawerHookResult {
  const api = useApi(StandardsShopClient);
  const { hasPermission } = useClaims();
  const canBrowse = hasPermission(ShiOnePermission.featuredProductsView);

  const isSupportedProductClass =
    request?.productClass != null &&
    SUPPORTED_PRODUCT_CLASSES.includes(request.productClass.toLowerCase());

  const fetchProductDetailDrawer =
    async (): Promise<ProductDetailDrawerDto | null> => {
      if (!request) return null;
      return await api.getStandardsProductDetailDrawer(request);
    };

  const query = useQuery({
    queryKey: [
      "standards-product-detail-drawer",
      {
        productDescriptor: request?.productDescriptor,
        productClass: request?.productClass?.toLowerCase(),
      },
    ],
    queryFn: fetchProductDetailDrawer,
    enabled:
      canBrowse &&
      request !== null &&
      request.productDescriptor != null &&
      isSupportedProductClass,
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
